import React, {Fragment} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {constants} from '@neosvoc/nv-platform-core-common';
import Yup from '../../yup';
import messages from '../../../res/translations/messages';
import Alert from '../Alert';
import Icon from '../Icon';
import FormGroupField from './FormGroupField';
import Error from '../Error';
import RolesField from './RolesField';
import PrivilegeBadges from './PrivilegeBadges';
import {TRACKERS_ARCHIVE_OPTIONS} from '../../constants';

const UserCreateSchema = Yup.object().shape({
    email: Yup.string().required().email(),
    name: Yup.string().required(),
    password: Yup.string().required(),
    password2: Yup.string().oneOf([Yup.ref('password'), null]).required(),
    roles: Yup.array().required().of(Yup.string().required().oneOf(Object.values(constants.USER_ROLES))),
    organization: Yup.object().required().shape({
        id: Yup.string(),
        name: Yup.string(),
        trackerArchiveFrequency: Yup.string().required().oneOf(Object.values(TRACKERS_ARCHIVE_OPTIONS)),
        trackerArchiveFrequencyValue: Yup.number().when('trackerArchiveFrequency', {
            is: (val) => val === 'trackerCreated',
            then: Yup.number().required().min(1),
            otherwise: Yup.number().required().min(0),
        }),
    }),
});

const UserCreateForm = ({error, intl, loading, organizations, onSubmit}) => {
    return (
        <Formik
            initialValues={{
                email: '',
                name: '',
                password: '',
                password2: '',
                organization: {
                    id: '',
                    name: '',
                    trackerArchiveFrequency: 'never',
                    trackerArchiveFrequencyValue: 0,
                },
                roles: [constants.USER_ROLES.READER],
            }}
            validationSchema={UserCreateSchema}
            onSubmit={onSubmit}
        >
            {({values, errors, touched, isValid}) => (
                <Fragment>
                    {error && <Alert error={error} />}
                    <Form>
                        <div className="survey-create-box">
                            <div className="col">
                                <FormGroupField type="email" id="user_create_form-email" name="email" label={messages.admin.users.email} required />
                                <FormGroupField type="text" id="user_create_form-name" name="name" label={messages.admin.users.name} required />
                                <FormGroupField type="password" id="user_create_form-password" name="password" label={messages.admin.users.password} required />
                                <FormGroupField type="password"
                                    id="user_create_form-password2" name="password2" label={messages.admin.users.password2} required />
                                <div className="form-group">
                                    <label className="col-form-label"><FormattedMessage {...messages.admin.users.roles} /></label>
                                    <Field name="roles" component={RolesField} id="user_edit_form-roles" />
                                    <ErrorMessage name="roles">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                                    <div className="mt-2">
                                        <span className="mr-2"><FormattedMessage {...messages.admin.users.effectivePrivileges} /></span>
                                        <PrivilegeBadges user={values} />
                                    </div>
                                </div>
                                <FormGroupField component="select" id="user_create_form-organization" name="organization.id"
                                    label={messages.admin.users.organization}>
                                    <option value="" className="font-italic">{intl.formatMessage(messages.admin.users.newOrganization)}</option>
                                    {(organizations || []).map(({id, name}) => <option value={id} key={id}>{name}</option>)}
                                </FormGroupField>
                                {values.organization.id ? null :
                                    <>
                                        <FormGroupField type="text" required id="user_create_form-organization_name" name="organization.name"
                                            label={messages.admin.users.newOrganizationName} />
                                    </>}
                            </div>

                            <div className="col text-right">
                                <Link className="btn btn-outline-secondary mr-3" to="/admin/users">
                                    <FormattedMessage {...messages.surveys.button.cancel} />
                                </Link>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    <Icon icon="new-doc" /> <FormattedMessage {...messages.surveys.button.create} />
                                </button>
                            </div>
                        </div>
                    </Form>
                </Fragment>
            )}
        </Formik>
    );
};

export default injectIntl(UserCreateForm);
